import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ref as dbRef, update as updateDb } from 'firebase/database';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiFileText } from 'react-icons/fi';
import { FaGraduationCap } from 'react-icons/fa';
import { db, storage } from '../../firebaseConfig';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import {
  certificateDownloadUrl,
  ensureLearningPathway,
  submitCourseCompletionProof,
  updateLearningPathwayProgress,
} from '../learningPathwaysApi';
import {
  CompletedPathwayRewardsPanel,
  CompletedPathwaysPanel,
  CompletedStepDetailPanel,
  CvDecodedPanel,
  FinalRewardsPanel,
  LoadingPanel,
  PathwayCompletePanel,
  PathwayIntroPanel,
  PathwayForm,
  PathwayOverviewPanel,
  PathwayProgressPanel,
  StartStepPanel,
  StepCompletePanel,
  StepMaterialsPanel,
} from '../learningPathways/LearningPathwaysPanels';
import {
  buildPathwaySteps,
  collectPathwayBadges,
  getDisplayName,
  isLearningPathwayReady,
  normalizeCompletedPathways,
} from '../learningPathways/pathwayData';
import AssessmentWorkspace from '../learningPathways/AssessmentWorkspace';
import { useLanguage } from '../contexts/LanguageContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { hasPaidAccess } from '../entitlements';
import { useLearningCopy } from '../learningPathways/learningLocale';
import UpgradeGate from '../components/UpgradeGate';
import { readUpgradeDraft, saveUpgradeDraft, clearUpgradeDraft, rememberUpgradeIntent } from '../upgrade/upgradeIntent';

const INITIAL_FORM = {
  targetJob: '',
  industry: '',
  coursePreference: '',
  targetCompanies: '',
};
const LATEST_LEARNING_PATHWAY_PROMPT_VERSION = 17;

function stageLabel(stage, copy) {
  if (stage === 'reading_cv') return copy('Reading your CV');
  if (stage === 'building_pathway') return copy('Building your base pathway');
  if (stage === 'finding_courses_and_projects') return copy('Finding courses and creating practice tasks');
  if (stage === 'preparing_assessments') return copy('Creating your assessments');
  if (stage === 'resolving_tools') return copy('Finding official tool learning pages');
  if (stage === 'finalizing') return copy('Saving your pathway');
  if (stage === 'completed') return copy('Your learning pathway is ready');
  if (stage === 'queued') return copy('Queued for generation');
  if (stage === 'error') return copy('Generation needs another try');
  return stage || '';
}

function formFromRecord(record, profile) {
  const onboarding = record?.onboardingData || {};
  return {
    targetJob: onboarding.targetJob || profile?.targetRole || '',
    industry: onboarding.industry || '',
    coursePreference: onboarding.coursePreference || '',
    targetCompanies: onboarding.targetCompanies || '',
  };
}

function initialPhaseFromRecord(record) {
  if (!record || record.generationStatus === 'error') return 'form';
  if (isLearningPathwayReady(record)) {
    return record.onboardingCompleted ? 'materials' : 'decoded';
  }
  if (record.generationStatus === 'in_progress') return 'generating';
  return 'form';
}

function LearningPathwaysScreen({ user, profile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [restoredForm] = useState(() => readUpgradeDraft(user?.uid, "pathway"));
  const formEdited = useRef(Boolean(restoredForm || location.state?.targetRole));
  const enteringNewGoal = useRef(Boolean(restoredForm || location.state?.targetRole));
  const { language } = useLanguage();
  const { entitlements } = useEntitlements();
  const paid = hasPaidAccess(entitlements);
  const copy = useLearningCopy();
  const pathwayPath = user?.uid ? `people/${user.uid}/learningPathway` : '';
  const completedPath = user?.uid ? `people/${user.uid}/completedPathways` : '';
  const cachedRecord = pathwayPath ? getCachedFirebaseValue(pathwayPath) : undefined;
  const cachedCompleted = completedPath ? getCachedFirebaseValue(completedPath) : undefined;
  const initialRecord = cachedRecord || null;
  const [record, setRecord] = useState(initialRecord);
  const [pathwayReady, setPathwayReady] = useState(!user?.uid || cachedRecord !== undefined);
  const [completedRaw, setCompletedRaw] = useState(cachedCompleted || null);
  const [phase, setPhase] = useState(() => restoredForm || location.state?.targetRole ? "form" : initialPhaseFromRecord(initialRecord));
  const [form, setForm] = useState(() => ({ ...formFromRecord(initialRecord, profile), ...restoredForm, ...(location.state?.targetRole ? { targetJob: location.state.targetRole } : {}) }));
  const [cvFile, setCvFile] = useState(null);
  const [cvStoragePath, setCvStoragePath] = useState(profile?.cvMetadata?.storagePath || '');
  const [working, setWorking] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');
  const [overviewIndex, setOverviewIndex] = useState(0);
  const [reward, setReward] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [selectedCompletedStep, setSelectedCompletedStep] = useState(null);
  const [selectedCompletedPathway, setSelectedCompletedPathway] = useState(null);
  const [regenerationRequested, setRegenerationRequested] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const recordReady = isLearningPathwayReady(record);

  useEffect(() => {
    setCvStoragePath(profile?.cvMetadata?.storagePath || '');
    setForm((current) => ({
      ...current,
      targetJob: current.targetJob || profile?.targetRole || '',
    }));
  }, [profile?.cvMetadata?.storagePath, profile?.targetRole]);

  useEffect(() => {
    const focusClass = 'js-learning-exercise-focus';
    const isExerciseOpen = phase === 'exercise';
    document.documentElement.classList.toggle(focusClass, isExerciseOpen);
    document.body.classList.toggle(focusClass, isExerciseOpen);

    return () => {
      document.documentElement.classList.remove(focusClass);
      document.body.classList.remove(focusClass);
    };
  }, [phase]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    if (getCachedFirebaseValue(pathwayPath) === undefined) setPathwayReady(false);
    const pathwayRef = dbRef(db, pathwayPath);
    const completedRef = dbRef(db, completedPath);
    const unsubscribePathway = subscribeCachedValue(pathwayRef, pathwayPath, (snapshot) => {
      const nextRecord = snapshot.exists() ? snapshot.val() : null;
      setRecord(nextRecord);
      setPathwayReady(true);
      if (nextRecord?.onboardingData && !formEdited.current) {
        setForm((current) => ({ ...current, ...formFromRecord(nextRecord, profile) }));
      }
    });
    const unsubscribeCompleted = subscribeCachedValue(completedRef, completedPath, (snapshot) => {
      setCompletedRaw(snapshot.exists() ? snapshot.val() : null);
    });
    return () => {
      unsubscribePathway();
      unsubscribeCompleted();
    };
  }, [user?.uid, profile, pathwayPath, completedPath]);

  useEffect(() => {
    if (enteringNewGoal.current) return;
    if (!record) {
      setRegenerationRequested(false);
      if (phase === 'exercise' && selectedExercise?.generationId) return;
      if (!['form', 'history', 'complete', 'pathway-complete', 'final-rewards', 'completed-rewards'].includes(phase)) {
        setPhase('form');
      }
      return;
    }
    if (recordReady) {
      if (phase === 'complete') return;
      setPhase(record.onboardingCompleted ? 'materials' : 'decoded');
      return;
    }
    if (record.generationStatus === 'error') {
      setPhase('form');
      return;
    }
    if (record.generationStatus === 'in_progress') {
      setPhase('generating');
      return;
    }
    // Only react to backend status changes here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordReady, record?.generationStatus, record?.onboardingCompleted]);

  useEffect(() => {
    const promptVersion = Number(record?.promptVersion || 0);
    const shouldRegenerate = recordReady
      && promptVersion < LATEST_LEARNING_PATHWAY_PROMPT_VERSION
      && record?.onboardingData;
    if (enteringNewGoal.current || !paid || !user?.uid || !shouldRegenerate || regenerationRequested) return;

    setRegenerationRequested(true);
    setError('');
    setPhase('generating');
    ensureLearningPathway({
      user,
      survey: formFromRecord(record, profile),
    }).catch((generationError) => {
      setError(language === 'ar' ? copy('Unable to refresh this pathway.') : generationError.message || copy('Unable to refresh this pathway.'));
      setPhase(record.onboardingCompleted ? 'materials' : 'decoded');
    });
  }, [copy, language, paid, profile, record, recordReady, regenerationRequested, user]);

  const completedPathways = useMemo(() => normalizeCompletedPathways(completedRaw), [completedRaw]);
  const steps = useMemo(() => buildPathwaySteps({
    ...(record?.pathwayData || {}),
    onboardingData: record?.onboardingData || {},
  }), [record]);
  const currentStepNumber = record?.currentStep || 1;
  const currentStep = steps[Math.max(0, currentStepNumber - 1)] || steps[0] || null;
  const currentProgress = (record?.stepProgress || {})[`step${currentStepNumber}`] || {};
  const completedSteps = useMemo(() => (
    Array.isArray(record?.completedSteps)
      ? record.completedSteps.map((item) => Number(item)).filter(Boolean)
      : []
  ), [record?.completedSteps]);
  const currentBadges = useMemo(() => collectPathwayBadges(record?.pathwayData || {}), [record?.pathwayData]);
  const hasCv = Boolean(cvStoragePath || profile?.cvMetadata?.storagePath || record?.cvStoragePath);
  const displayName = getDisplayName(profile, user);
  const isAssessedPathway = Number(record?.promptVersion || 0) >= 8;

  const survey = {
    targetJob: form.targetJob.trim(),
    industry: form.industry.trim(),
    coursePreference: form.coursePreference.trim(),
    targetCompanies: form.targetCompanies.trim(),
  };

  const uploadCvIfNeeded = async () => {
    if (hasCv) return cvStoragePath || profile?.cvMetadata?.storagePath;
    if (!cvFile) throw new Error(copy('Upload a PDF CV to continue.'));
    if (cvFile.type !== 'application/pdf') throw new Error(copy('Upload a PDF CV to continue.'));

    const storagePath = `Images/${user.uid}/cv.pdf`;
    const fileRef = storageRef(storage, storagePath);
    await uploadBytes(fileRef, cvFile);
    const cvLink = await getDownloadURL(fileRef);
    await updateDb(dbRef(db, `people/${user.uid}`), {
      cv: { Link: cvLink },
      cvMetadata: {
        name: cvFile.name,
        size: cvFile.size,
        type: cvFile.type,
        storagePath,
        capturedAt: Date.now(),
      },
    });
    setCvStoragePath(storagePath);
    return storagePath;
  };

  const validateForm = () => {
    if (!survey.targetJob || !survey.industry || !survey.coursePreference) {
      throw new Error(copy('Add a target job, industry, and course preference.'));
    }
  };

  const handleStartGeneration = async () => {
    setError('');
    try {
      validateForm();
      if (!hasCv && !cvFile) throw new Error(copy('Upload a PDF CV to continue.'));
      if (cvFile && cvFile.type !== 'application/pdf') throw new Error(copy('Upload a PDF CV to continue.'));
      if (!paid) {
        setShowUpgrade(true);
        return;
      }
      setWorking(true);
      await uploadCvIfNeeded();
      enteringNewGoal.current = false;
      clearUpgradeDraft(user?.uid, 'pathway');
      setPhase('generating');
      await ensureLearningPathway({ user, survey });
    } catch (generationError) {
      setError(language === 'ar' ? copy('Unable to start pathway generation.') : generationError.message || copy('Unable to start pathway generation.'));
      setPhase('form');
    } finally {
      setWorking(false);
    }
  };

  const handleBeginMaterials = async () => {
    setBusyAction('begin');
    setError('');
    try {
      await updateLearningPathwayProgress({ user, action: 'onboarding_complete' });
      setPhase('materials');
    } catch (progressError) {
      setError(language === 'ar' ? copy('Unable to start this pathway.') : progressError.message || copy('Unable to start this pathway.'));
    } finally {
      setBusyAction('');
    }
  };

  const handleCourseOpen = (course, index) => {
    setError('');
    return updateLearningPathwayProgress({
      user,
      action: 'course_click',
      stepNumber: currentStepNumber,
      courseId: `${currentStepNumber}-${index}-${course.link || course.course_name}`,
    }).catch((courseError) => {
      setError(language === 'ar' ? copy('The course opened, but we could not save it as your selection. Please try again.') : courseError.message || copy('The course opened, but we could not save it as your selection. Please try again.'));
    });
  };

  const handleCoursesComplete = async () => {
    setBusyAction('courses');
    setError('');
    try {
      const result = await updateLearningPathwayProgress({
        user,
        action: 'courses_complete',
        stepNumber: currentStepNumber,
      });
      if (result.completedStep) {
        setReward(result.completedStep);
        setPhase(result.pathwayCompleted ? 'pathway-complete' : 'complete');
      } else {
        setSelectedExercise(null);
        setPhase('materials');
      }
    } catch (progressError) {
      setError(language === 'ar' ? copy('Unable to update course progress.') : progressError.message || copy('Unable to update course progress.'));
    } finally {
      setBusyAction('');
    }
  };

  const handleCourseProofSubmit = async ({ courseId, courseTitle, proofUrl, file }) => {
    setBusyAction('proof');
    setError('');
    try {
      return await submitCourseCompletionProof({
        user,
        stepNumber: currentStepNumber,
        courseId,
        courseTitle,
        proofUrl,
        file,
      });
    } catch (proofError) {
      setError(language === 'ar' ? copy('Unable to submit proof of completion.') : proofError.message || copy('Unable to submit proof of completion.'));
      throw proofError;
    } finally {
      setBusyAction('');
    }
  };

  const handleCourseProofFallback = async () => {
    setBusyAction('proof-fallback');
    setError('');
    try {
      return await updateLearningPathwayProgress({
        user,
        action: 'continue_without_certificate',
        stepNumber: currentStepNumber,
      });
    } catch (proofError) {
      setError(language === 'ar' ? copy('Unable to continue without a certificate.') : proofError.message || copy('Unable to continue without a certificate.'));
      throw proofError;
    } finally {
      setBusyAction('');
    }
  };

  const handleContinueAfterReward = () => {
    setReward(null);
    setPhase(record ? 'progress' : 'history');
  };

  const handleStartNew = () => {
    setReward(null);
    setError('');
    setForm(INITIAL_FORM);
    setPhase('form');
  };

  const handleOpenExercise = (_, exerciseIndex) => {
    setSelectedExercise({
      exerciseIndex,
      generationId: record?.generationId,
      stepNumber: currentStepNumber,
    });
    setPhase('exercise');
  };

  const handleAssessmentPassed = (result) => {
    setReward(result?.reward || null);
    setSelectedExercise(null);
    setPhase(result?.pathwayCompleted ? 'pathway-complete' : 'complete');
  };

  const handleOpenCompletedStep = (stepNumber) => {
    setSelectedCompletedStep(stepNumber);
    setPhase('completed-step');
  };

  const handleCloseExercise = () => {
    setSelectedExercise(null);
    setPhase('materials');
  };

  const latestCompletedPathway = completedPathways[0] || null;
  const finalBadges = latestCompletedPathway?.allBadges?.length
    ? latestCompletedPathway.allBadges
    : currentBadges;
  const finalIsAssessed = Number(latestCompletedPathway?.promptVersion || record?.promptVersion || 0) >= 8
    || reward?.assessedPathway === true
    || reward?.credentialType === 'assessed_skill_badge'
    || reward?.credentialType === 'assessed_pathway_certificate';
  const finalCredentialsIssued = Boolean(reward?.credentialIssued || reward?.pathwayCertificate?.credentialIssued);

  const pathwayForm = (
    <PathwayForm
      form={form}
      setForm={(next) => { formEdited.current = true; setForm(next); }}
      hasCv={hasCv}
      cvFile={cvFile}
      onFileChange={(event) => setCvFile(event.target.files?.[0] || null)}
      onSubmit={handleStartGeneration}
      working={working}
      error={record?.generationStatus === 'error' ? language === 'ar' ? copy('We could not finish your learning pathway. Please try again.') : record.stageMessage || record.error || error : error}
      completedPathways={paid ? completedPathways : []}
      onOpenCompleted={() => setPhase('history')}
    />
  );

  if (!paid && showUpgrade) {
    return (
      <UpgradeGate
        user={user}
        context="learning_pathways"
        onBack={() => setShowUpgrade(false)}
        backLabel={language === 'ar' ? 'العودة إلى إعداد مسار التعلّم' : 'Back to Learning Pathways'}
        onCheckout={async () => {
          saveUpgradeDraft(user?.uid, 'pathway', form);
          await uploadCvIfNeeded();
          rememberUpgradeIntent(user?.uid, { path: '/learning-pathways' });
        }}
      />
    );
  }

  if (!paid) return pathwayForm;

  if (!pathwayReady) {
    return (
      <LoadingPanel
        icon={<FiFileText />}
        title={copy('Checking your learning pathway')}
        message={copy("We're loading the latest pathway status from your profile.")}
      />
    );
  }

  if (phase === 'history') {
    return (
      <CompletedPathwaysPanel
        completedPathways={completedPathways}
        onStartNew={handleStartNew}
        onBack={() => navigate('/opportunities')}
        onOpenRewards={(pathway) => {
          setSelectedCompletedPathway(pathway);
          setPhase('completed-rewards');
        }}
      />
    );
  }

  if (phase === 'completed-rewards') {
    return (
      <CompletedPathwayRewardsPanel
        pathway={selectedCompletedPathway}
        onBack={() => setPhase('history')}
      />
    );
  }

  if (phase === 'pathway-complete') {
    return (
      <PathwayCompletePanel
        assessed={finalIsAssessed}
        credentialsIssued={finalCredentialsIssued}
        onClaimRewards={() => setPhase('final-rewards')}
      />
    );
  }

  if (phase === 'final-rewards') {
    return (
      <FinalRewardsPanel
        badges={finalIsAssessed && !finalCredentialsIssued ? [] : finalBadges}
        assessed={finalIsAssessed}
        credentialsIssued={finalCredentialsIssued}
        downloadUrl={reward?.credentialIssued && reward?.certificateTitle && reward?.verifyUrl ? certificateDownloadUrl({
          name: displayName,
          skill: reward.certificateTitle,
          verifyUrl: reward.verifyUrl,
        }) : ''}
        onApply={() => navigate('/opportunities')}
        onCompleted={() => setPhase('history')}
      />
    );
  }

  if (phase === 'complete') {
    return (
      <StepCompletePanel
        reward={reward}
            downloadUrl={reward?.verifyUrl && reward?.certificateTitle ? certificateDownloadUrl({
              name: displayName,
              skill: reward?.certificateTitle,
              verifyUrl: reward?.verifyUrl,
            }) : ''}
        onContinue={handleContinueAfterReward}
      />
    );
  }

  if ((!record && phase !== 'exercise') || phase === 'form') {
    return pathwayForm;
  }

  if (!recordReady && (phase === 'generating' || record.generationStatus === 'in_progress')) {
    return (
      <LoadingPanel
        icon={<FaGraduationCap />}
        title={copy('Creating your learning pathway')}
        message={copy("This can take a few minutes because we're finding real courses and creating practice tasks for your exact goal.")}
        stage={record.stage}
        stageText={language === 'ar' ? stageLabel(record.stage, copy) : record.stageLabel || stageLabel(record.stage, copy)}
        statusMessage={language === 'ar' ? stageLabel(record.stage, copy) : record.stageMessage || record.generationProgress?.message}
        progress={record.generationProgress}
        survey={record.onboardingData || survey}
        canLeave
        onBrowseJobs={() => navigate('/opportunities')}
        onCareerCoach={() => navigate('/career-coach')}
      />
    );
  }

  if (phase === 'decoded') {
    return (
      <CvDecodedPanel
        pathwayData={record.pathwayData || {}}
        onBack={() => navigate('/opportunities')}
        onNext={() => setPhase('overview')}
      />
    );
  }

  if (phase === 'overview') {
    return (
      <PathwayOverviewPanel
        steps={steps}
        currentIndex={overviewIndex}
        setCurrentIndex={setOverviewIndex}
        onStart={() => setPhase('intro')}
      />
    );
  }

  if (phase === 'intro') {
    return <PathwayIntroPanel onBegin={() => setPhase('start')} />;
  }

  if (phase === 'start') {
    return <StartStepPanel step={currentStep} assessed={isAssessedPathway} onBegin={handleBeginMaterials} />;
  }

  if (phase === 'progress') {
    return (
      <PathwayProgressPanel
        steps={steps}
        currentStepNumber={currentStepNumber}
        completedSteps={completedSteps}
        onContinue={() => setPhase('start')}
      />
    );
  }

  if (phase === 'exercise') {
    return (
      <AssessmentWorkspace
        user={user}
        stepNumber={selectedExercise?.stepNumber || currentStepNumber}
        exerciseIndex={selectedExercise?.exerciseIndex}
        generationId={selectedExercise?.generationId}
        onBack={handleCloseExercise}
        onPassed={handleAssessmentPassed}
      />
    );
  }

  if (phase === 'completed-step') {
    const stepNumber = selectedCompletedStep || completedSteps[completedSteps.length - 1];
    const selectedStep = steps.find((item) => item.number === stepNumber);
    const selectedProgress = (record?.stepProgress || {})[`step${stepNumber}`] || {};
    return (
      <CompletedStepDetailPanel
        step={selectedStep}
        progress={selectedProgress}
        onBack={() => setPhase('materials')}
      />
    );
  }

  return (
    <StepMaterialsPanel
      step={currentStep}
      assessed={isAssessedPathway}
      steps={steps}
      completedSteps={completedSteps}
      currentStepNumber={currentStepNumber}
      progress={currentProgress}
      onCourseOpen={handleCourseOpen}
      onCourseProofSubmit={handleCourseProofSubmit}
      onCourseProofFallback={handleCourseProofFallback}
      onCoursesComplete={handleCoursesComplete}
      onExerciseOpen={handleOpenExercise}
      onViewPath={handleOpenCompletedStep}
      busyAction={busyAction}
      error={error}
    />
  );
}

export default LearningPathwaysScreen;
